import {
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  createContext,
  useContext,
} from "react";

type NavigateFn = (href: string) => void;

const NavigationContext = createContext<NavigateFn | null>(null);

/**
 * Makes the design-system link components (Link, NavElement, Button with
 * href) navigate through the host app's router instead of a full page load.
 * Wrap the app once, inside the router's context:
 *
 * ```tsx
 * <NavigationProvider navigate={(href) => router.navigate(href)}>
 *   <App />
 * </NavigationProvider>
 * ```
 *
 * Without a provider, links fall back to regular browser navigation.
 */
export function NavigationProvider({
  navigate,
  children,
}: {
  navigate: NavigateFn;
  children: ReactNode;
}) {
  return (
    <NavigationContext.Provider value={navigate}>
      {children}
    </NavigationContext.Provider>
  );
}

function isExternal(href: string) {
  return /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//");
}

/**
 * Returns an anchor onClick handler that defers to the host router when one
 * is provided, while preserving browser behaviour for modified clicks, new
 * tabs and external URLs.
 */
export function useLinkClickHandler(
  href: string | undefined,
  onClick?: MouseEventHandler<HTMLAnchorElement>,
): MouseEventHandler<HTMLAnchorElement> {
  const navigate = useContext(NavigationContext);
  return (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      !navigate ||
      !href ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      isExternal(href)
    ) {
      return;
    }
    const target = event.currentTarget.getAttribute("target");
    if (target && target !== "_self") return;
    event.preventDefault();
    navigate(href);
  };
}
